import { useQueries } from '@tanstack/react-query';
import { getTextMatchCdnUrl } from 'utils/textMatchCdn';

/** Compact representation of one side of a match: [page, left, top, right, bottom] */
export type CompactMatchSide = [page: number, left: number, top: number, right: number, bottom: number];

/** Compact representation of a full match: [[master side], [sample side]] */
export type CompactMatch = [master: CompactMatchSide, sample: CompactMatchSide];

/** Fetches compact matches for a specific page from CDN */
async function fetchTextMatchPage(
  inspectionId: string,
  ctfJobId: string,
  side: 'master' | 'sample',
  page: number,
  cdnPathPrefix?: string | null,
): Promise<CompactMatch[]> {
  const url = getTextMatchCdnUrl(inspectionId, ctfJobId, `${side}/${page}.json`, cdnPathPrefix);
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Failed to fetch text match page: ${response.status}`);
  }
  return response.json();
}

/**
 * Hook to fetch text match pages with active observers.
 * Fetches all specified pages in parallel and caches each one individually.
 * Having active observers prevents React Query from garbage collecting the cache entries.
 */
export const useTextMatchPages = (
  inspectionId: string | undefined,
  ctfJobId: string | null | undefined,
  pages: { side: 'master' | 'sample'; page: number }[],
  cdnPathPrefix?: string | null,
) => {
  return useQueries({
    queries: pages.map(({ side, page }) => ({
      queryKey: ['textMatchPage', inspectionId, ctfJobId, side, page, cdnPathPrefix],
      queryFn: () => fetchTextMatchPage(inspectionId!, ctfJobId!, side, page, cdnPathPrefix),
      enabled: !!inspectionId && !!ctfJobId,
      staleTime: Infinity,
    })),
  });
};
